import React, { useEffect, useState } from 'react';
import { Row, Col } from "react-bootstrap";
import PropTypes from 'prop-types';
import { SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem } from '../../../sessionStorage';
import { Card, CardContent, lighten, makeStyles, CardActions } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import TestimonialsData from './TestimonialsData';
import Carousel from '../../Helpers/Widgets/AutoSwipeCarousel/AutoSwipeCarousel';
import TestimonialGraphicModal from './TestimonialGraphicModal';
import './Testimonials.scss';
import States from '../../Helpers/Lists/States';
import { format } from 'date-fns';
import NumberFormat from 'react-number-format';
import StarIcon from '../../Helpers/Svg/StarIcon';
import { handleStarRating } from './TestimonialHandler';

function TestimonialCard(props) {
  const [expand, setExpand] = useState(false);
  const testimonialText = document.getElementById(`testimonial-text-${props.index}`);
  const testimonialTextHeight = testimonialText && testimonialText.offsetHeight
  let testimonialCard = document.getElementById(`testimonial-card-${props.index}`);
  const CardTextMinVerticalSpace = 51; // based on height of one line
  const state = States().map(s => s.value === props.data.customerState ? s.label : "");
  const coverageAmount = props.data.coverageAmount ? <NumberFormat displayType={'text'} prefix={'$'} value={props.data.coverageAmount} thousandSeparator={true} /> : '';
  const monthlyRate = props.data.monthlyRate ?  <NumberFormat value={props.data.monthlyRate} displayType={'text'} thousandSeparator={true} decimalScale={2} fixedDecimalScale={true}/> : '';

  useEffect(() => {
    if (testimonialCard && testimonialTextHeight > CardTextMinVerticalSpace) {
      setExpand(false);
      const cardHeight = window.innerWidth > 390 ? '186px' : '190px';
      testimonialCard.style.height = cardHeight;
    }
  }, [props.childExpand])

  const handleExpandHeight = () => {
    if (testimonialCard && testimonialTextHeight > CardTextMinVerticalSpace) {
      if (!expand) {
        testimonialCard.style.height = `${testimonialCard.offsetHeight + testimonialTextHeight}px`; // Set the height in pixels
      }
      else {
        testimonialCard.style.height = `${testimonialCard.offsetHeight - testimonialTextHeight}px`; // Set the height in pixels
      }
      setExpand(!expand)
    }
  }

  return (
    <Card className={`testimonial-card`} id={`testimonial-card-${props.index}`}>
      <CardContent className='testimonial-card-content'>
        <Row className="testimonial-header">
          <div className='d-flex align-items-center'>
            <div className={`${props.classes.initialsBackGround} initials`}> {props.data.customerInitials}</div>
            <Col className="star-rating-container" >
              <Rating name="half-rating-read" defaultValue={handleStarRating(props.data.starRating)} className="star-rating-icon" precision={0.1} readOnly emptyIcon={<StarIcon className='empty-star' fontSize="inherit" />}/>
              <p className="consumer-name" > {props.data.customerFirstName} - {state}</p>
            </Col>
          </div>
          <div className='card-logo'>
            <img className="carrier-logo" src={props.data.carrierLogoUrl} alt="Carrier Logo" />
          </div>
        </Row>
        <div className="testimonial-body">
          <p className="coverage-details">
          {coverageAmount} - {props.data.coverageType}, {monthlyRate}/mo
          </p>
          <div className="testimonial-text" id={`testimonial-text-${props.index}`}>
            {props.data.testimonial}
          </div>
        </div>
        <CardActions disableSpacing className='card-action-container'>
          <div className='d-flex card-action-div'>
            <div className="testimonial-date"> { format(new Date(props.data.testimonialDate), 'MMM. dd, yyyy')}</div>
            {testimonialTextHeight > CardTextMinVerticalSpace && <a className={`${props.classes.primaryLink} expand-action`} onClick={() => handleExpandHeight()}>{expand ? "show less" : "show more"} </a>}
          </div>
        </CardActions>
      </CardContent>
    </Card>
  );
}

export default function Testimonials(props) {
  const useStyles = makeStyles(theme => ({
    initialsBackGround: {
      backgroundColor: lighten(theme.palette.primary.main, 0.92),
      color: theme.palette.primary.main,
    },
    primaryLink: {
      color: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.main,
      },
      '&:focus': {
        color: theme.palette.primary.main,
      },
    }
  }));

  const classes = useStyles();
  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const testimonialPanelHeader = accountObj && accountObj.account && accountObj.account.testimonialPanelHeader ? accountObj.account.testimonialPanelHeader : 'Our members love their life insurance experience!';
  const testimonialsList = accountObj && accountObj.testimonials && accountObj.testimonials.length > 0 ? accountObj.testimonials : TestimonialsData();
  const avgStarRating = 4.8;
  const [childExpand, setChildExpand] = useState(false);
  const cardData = testimonialsList && testimonialsList.map((d, index) => <TestimonialCard index={index} data={d} classes={classes} childExpand={childExpand} setChildExpand={(value) => setChildExpand(value)} />)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showGraphicModal, setShowGraphicModal] = useState(false);
  let cardsForDots = [];

  const getNoOfCardAsPerScreen = () => {
    cardsForDots = [];
    const screenSize = window.innerWidth;
    let noOfCards = 0;
    if (screenSize >= 1200) {
      noOfCards = 3;
    } else if (screenSize >= 768 && screenSize < 1200) {
      noOfCards = 2;
    } else if (screenSize < 768) {
      noOfCards = 1;
    }
    for (let i = 0; i < cardData.length; i += noOfCards) {
      cardsForDots.push(cardData.slice(i, i + noOfCards));
    }
    return noOfCards;
  }

  const [cardPerScreen, setCardPerScreen] = useState(getNoOfCardAsPerScreen());

  const changeCurrentIndex = (side) => {
    if (side === 'NEXT') {
      if (currentIndex === cardsForDots.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    } else {
      if (currentIndex === 0) {
        setCurrentIndex(cardsForDots.length - 1);
      } else {
        setCurrentIndex(currentIndex - 1);
      }
    }
    setChildExpand(!childExpand)
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize)
  }, []);

  const handleResize = () => {
    const cardNumber = getNoOfCardAsPerScreen();
    setCardPerScreen(cardNumber);
    const indexValue = Math.floor(currentIndex / cardNumber)
    setCurrentIndex(indexValue)
  }

  return (
    <div className="testimonials">
      <Row className="panel-header">
        <h2 className={`${props.isShowPanel ? 'panel-home-title' : 'panel-title'}`}>{testimonialPanelHeader}</h2 >
        <h2 className={`${props.isShowPanel ? 'panel-home-subtitle' : 'panel-subtitle'} mb-3`}>Over 500+ customer satisfaction surveys</h2>
        <div className="star-rating-panel" onClick={() => setShowGraphicModal(true)}>
          <Rating name="half-rating-read" className="star-rating-icon" defaultValue={handleStarRating(avgStarRating)} precision={0.1} readOnly size="large" emptyIcon={<StarIcon className='empty-star' fontSize="inherit" />} />
          <p className={`${classes.primaryLink} avg-star-rate m-0`}>{avgStarRating} out of 5 stars</p>
        </div>
      </Row>
      {props.isShowCard && <Row className="testimonial-carousel-div pt-0 mb-3">
        <Carousel
          items={cardData}
          numItems={cardData.length}
          showArrow={true}
          changeCurrentIndex={(side) => changeCurrentIndex(side)}
          currentIndex={currentIndex}
          indicators={true}
          isAutoCarousel={false}
          isDOtContainer={true}
          cardPerScreen={cardPerScreen}
          cardsForDots={cardsForDots}
          iconWidth={24}
          swipeAble={cardData.length > cardPerScreen ? 'swipeAble' : 'swipeAbleAutoWith'}
          carouselArrow={'carouselArrow'}
        />
      </Row>}
      {showGraphicModal && <TestimonialGraphicModal show={showGraphicModal} onHide={() => setShowGraphicModal(false)} avgStarRating={avgStarRating} testimonialsList={testimonialsList} />}
    </div>
  );
}

Testimonials.propTypes = {
  testimonials: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })),
}


// WEBPACK FOOTER //
// ./src/components/Common/Testimonials/Testimonials.js